import { mapApiHousehold } from "../mappers/mapApiHousehold";
import api from "@/plugins/api";
// ─── API functions ─────────────────────────────────────────────────────────────
// GET /contacts/{contactId}/household
// Returns null when the contact has no household yet.
export async function getHousehold(contactId) {
    const response = await api.get(`/contacts/${contactId}/household`, { unwrapEnvelope: true });
    if (!response.data?.household)
        return null;
    return mapApiHousehold(response.data.household);
}
// POST /households
// Name is derived from the principal member's full name on the FE.
export async function createHousehold(payload) {
    await api.post("/households", {
        name: payload.name,
        householdType: payload.householdType,
        greetingFormula: payload.greetingFormula ?? null,
        members: payload.members.map((m) => ({
            contactId: m.contactId,
            role: m.role,
            isPrincipal: m.isPrincipal,
        })),
    }, { unwrapEnvelope: true });
}
// PUT /households/{householdId} + member reconciliation via member endpoints.
// Updates household metadata then reconciles members (remove → update → add).
// Removals are sequential to avoid triggering the last-member guard mid-batch.
// NOTE: reconciliation is non-atomic (up to 1+N+1+N+N HTTP calls). A mid-sequence
// failure leaves the household partially updated with no automatic rollback.
// The caller should reload household state after any error to surface the actual persisted state.
export async function updateHousehold(householdId, existingMembers, payload) {
    await api.put(`/households/${householdId}`, {
        name: payload.name,
        householdType: payload.householdType,
        greetingFormula: payload.greetingFormula ?? null,
        email: payload.principalEmail ?? null,
    }, { unwrapEnvelope: true });
    const keepIds = new Set(payload.members.filter((m) => m.id).map((m) => m.id));
    for (const m of existingMembers.filter((m) => !keepIds.has(m.id))) {
        await api.delete(`/households/${householdId}/members/${m.id}`, { unwrapEnvelope: true });
    }
    // Promote the principal first — OPHUB rejects demoting the old one if no principal exists yet
    // (business rule: exactly one principal at all times).
    // If the new principal is an existing member (has id), promote via PUT.
    // If the new principal is a new member (no id), POST them first so the old principal can be safely demoted.
    const principal = payload.members.find((m) => m.isPrincipal);
    if (principal?.id) {
        await api.put(`/households/${householdId}/members/${principal.id}`, { role: principal.role, isPrimaryContact: true }, { unwrapEnvelope: true });
    }
    else if (principal) {
        await api.post(`/households/${householdId}/members`, { contactId: principal.contactId, role: principal.role, isPrimaryContact: true }, { unwrapEnvelope: true });
    }
    await Promise.all(payload.members
        .filter((m) => m.id && !m.isPrincipal)
        .map((m) => api.put(`/households/${householdId}/members/${m.id}`, { role: m.role, isPrimaryContact: false }, { unwrapEnvelope: true })));
    await Promise.all(payload.members
        .filter((m) => !m.id && !m.isPrincipal)
        .map((m) => api.post(`/households/${householdId}/members`, { contactId: m.contactId, role: m.role, isPrimaryContact: false }, { unwrapEnvelope: true })));
}
// DELETE /households/{householdId}
// Removes the household; member contact records are NOT deleted.
export async function deleteHousehold(householdId) {
    await api.delete(`/households/${householdId}`, { unwrapEnvelope: true });
}
